import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import { useDashboardSummary, useParticipants } from "@/api/hooks";
import type { Participant } from "@/api/types";
import { Users, Upload, Activity, TriangleAlert, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { WebAlertNotifications } from "@/components/web-alert-notifications";
import { UploadAlertDialog } from "@/components/sensor-charts/upload-alert-dialog";
import { relativeTime } from "@/lib/format-date";
import {
  getUploadAlertBadgeLabel,
  getUploadAlertDescription,
  getUploadAlertKey,
  getUploadAlertReferenceTime,
  getUploadAlertSeverity,
  getUploadAlertWindowLabel,
  hasUploadAlert,
} from "@/lib/upload-alerts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const DISMISSED_ALERT_SIGNATURE_KEY = "edgecare-dismissed-dashboard-alerts";

export default function DashboardPage() {
  const summary = useDashboardSummary();
  const participants = useParticipants({ monitorAlerts: true });
  const [dismissedAlertSignature, setDismissedAlertSignature] = useState(
    () => sessionStorage.getItem(DISMISSED_ALERT_SIGNATURE_KEY) ?? "",
  );
  const [includeAllParticipants, setIncludeAllParticipants] = useState(false);
  const [selectedAlertParticipant, setSelectedAlertParticipant] =
    useState<Participant | null>(null);

  if (summary.isLoading || participants.isLoading) return <Spinner />;
  if (summary.isError)
    return (
      <ErrorDisplay
        message={summary.error.message}
        onRetry={() => summary.refetch()}
      />
    );
  if (participants.isError)
    return (
      <ErrorDisplay
        message={participants.error.message}
        onRetry={() => participants.refetch()}
      />
    );
  if (!summary.data) return <EmptyState message="No dashboard data available" />;
  if (!participants.data) return <EmptyState message="No participant data available" />;

  const { data } = summary.data;
  const allAlertedParticipants = participants.data.data
    .filter((participant) =>
      hasUploadAlert(participant, { includeAllParticipants }),
    )
    .sort((a, b) => {
      const severityDiff =
        getUploadAlertSeverity(a) - getUploadAlertSeverity(b);
      if (severityDiff !== 0) return severityDiff;
      const aTime = getUploadAlertReferenceTime(a);
      const bTime = getUploadAlertReferenceTime(b);
      return (
        (aTime ? new Date(aTime).getTime() : 0) -
        (bTime ? new Date(bTime).getTime() : 0)
      );
    });
  const alertSignature = allAlertedParticipants
    .map(getUploadAlertKey)
    .join("|");
  const alertsDismissed =
    alertSignature !== "" && alertSignature === dismissedAlertSignature;
  const alertedParticipants = alertsDismissed ? [] : allAlertedParticipants;

  function resetAlertsForNow() {
    sessionStorage.setItem(DISMISSED_ALERT_SIGNATURE_KEY, alertSignature);
    setDismissedAlertSignature(alertSignature);
  }

  const sourceChartData = Object.entries(data.uploads_by_source).map(
    ([source, count]) => ({ source, count })
  );

  const dailyChartData = Object.entries(data.daily_uploads)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));

  const statCards = [
    {
      label: "Total Participants",
      value: data.total_participants,
      icon: Users,
    },
    { label: "Total Uploads", value: data.total_uploads, icon: Upload },
    { label: "Active Today", value: data.active_today, icon: Activity },
  ];

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#1E293B]">Dashboard</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Alert scope"
            className="inline-flex items-center self-start rounded-lg border border-border bg-muted/40 p-0.5 sm:self-auto"
          >
            <Button
              type="button"
              size="sm"
              variant={!includeAllParticipants ? "default" : "ghost"}
              className="h-7 px-3 text-xs"
              onClick={() => setIncludeAllParticipants(false)}
              title="Show only real participants with IDs edgeXXX or EDGEXXX"
            >
              Real (edgeXXX)
            </Button>
            <Button
              type="button"
              size="sm"
              variant={includeAllParticipants ? "default" : "ghost"}
              className="h-7 px-3 text-xs"
              onClick={() => setIncludeAllParticipants(true)}
              title="Show all participants"
            >
              All
            </Button>
          </div>
          <WebAlertNotifications
            participants={allAlertedParticipants}
            dismissedAlertSignature={dismissedAlertSignature}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1E293B]">
                {stat.value.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {alertedParticipants.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <TriangleAlert className="size-4 text-amber-700" />
                <CardTitle className="text-amber-900">
                  Upload Alerts
                </CardTitle>
              </div>
              <Badge className="bg-amber-100 text-amber-900 border-amber-300">
                {alertedParticipants.length} flagged
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-amber-900">
                Alerts trigger when{" "}
                {includeAllParticipants
                  ? "any participant"
                  : "an active edgeXXX / EDGEXXX participant"}{" "}
                with at least 1 upload has no upload in 24 hours, or a 1 hour
                gap in captured periodic data during the last 24 hours. Click an
                alert card to inspect the capture timeline and gap plot.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                  onClick={resetAlertsForNow}
                >
                  Reset for now
                </Button>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {alertedParticipants.map((participant) => {
                const gapWindow = getUploadAlertWindowLabel(participant);
                return (
                  <button
                    key={participant.user_id}
                    type="button"
                    onClick={() => setSelectedAlertParticipant(participant)}
                    className="group rounded-lg border border-amber-200 bg-white p-3 text-left transition-all hover:border-amber-400 hover:shadow-sm cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-mono text-xs font-medium text-slate-900">
                        {participant.username || participant.user_id.slice(0, 8)}
                      </div>
                      <Badge className="bg-red-100 text-red-800 border-red-200">
                        {getUploadAlertBadgeLabel(participant)}
                      </Badge>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {participant.total_uploads.toLocaleString()} uploads
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      {getUploadAlertDescription(participant)}
                    </div>
                    {gapWindow && (
                      <div className="mt-1.5 rounded bg-amber-50 px-2 py-1 font-mono text-[11px] text-amber-900">
                        Gap window: {gapWindow}
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>
                        {participant.last_uploaded_at
                          ? `Last upload ${relativeTime(participant.last_uploaded_at)}`
                          : "No recent upload"}
                      </span>
                      <span className="inline-flex items-center gap-1 font-medium text-amber-800 group-hover:underline">
                        <BarChart3 className="size-3.5" />
                        View gap plot
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <UploadAlertDialog
        participant={selectedAlertParticipant}
        open={Boolean(selectedAlertParticipant)}
        onOpenChange={(open) => {
          if (!open) setSelectedAlertParticipant(null);
        }}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Weekly Uploads by Source</CardTitle>
          </CardHeader>
          <CardContent>
            {sourceChartData.length === 0 ? (
              <EmptyState message="No upload data yet" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="source"
                    tick={{ fontSize: 11 }}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Weekly Upload Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyChartData.length === 0 ? (
              <EmptyState message="No daily data yet" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
